/** Combine Annual Returns P&L and cash charts into one grouped (two-series) column chart. */

export type AnnualAbsSideRow = {
  side: string;
  Gross: number;
};

export type AnnualAbsCurrencyChart = {
  currency: string;
  sideRows: AnnualAbsSideRow[];
};

export type AnnualCombinedSide = 'In' | 'Out' | 'Net';

export type AnnualCombinedSideRow = {
  side: AnnualCombinedSide;
  pl: number;
  cash: number;
};

export type AnnualCombinedCurrencyChart = {
  currency: string;
  sideRows: AnnualCombinedSideRow[];
  /** True when GBP totals were converted and added into this EUR chart. */
  gbpConverted?: boolean;
  gbpToEurRate?: number;
};

/** Same display rate as Balances / Transactions GBP→EUR equivalents. */
export const DEFAULT_GBP_TO_EUR_RATE = 1.16;

const roundMoney = (n: number): number => Number(n.toFixed(2));

/** GBP → EUR at the display rate; other currencies stay 1:1 so they still land on one EUR chart. */
export const eurScaleForCurrency = (
  currency: string | null | undefined,
  gbpToEurRate: number = DEFAULT_GBP_TO_EUR_RATE
): number => {
  const ccy = String(currency || 'EUR').trim().toUpperCase() || 'EUR';
  if (ccy === 'EUR') return 1;
  if (ccy === 'GBP') {
    const rate = Number(gbpToEurRate);
    return Number.isFinite(rate) && rate > 0 ? rate : DEFAULT_GBP_TO_EUR_RATE;
  }
  return 1;
};

/** Multiply an amount in `fromCurrency` to express it in `toCurrency` (via EUR). */
export const scaleToCurrency = (
  fromCurrency: string | null | undefined,
  toCurrency: string | null | undefined,
  gbpToEurRate: number = DEFAULT_GBP_TO_EUR_RATE
): number => {
  const to = String(toCurrency || 'EUR').trim().toUpperCase() || 'EUR';
  const toScale = eurScaleForCurrency(to, gbpToEurRate);
  if (!(toScale > 0)) return 1;
  return eurScaleForCurrency(fromCurrency, gbpToEurRate) / toScale;
};

const grossForSide = (chart: AnnualAbsCurrencyChart | undefined, side: string): number => {
  const row = chart?.sideRows.find((r) => r.side === side);
  const n = Number(row?.Gross);
  return Number.isFinite(n) ? n : 0;
};

const combinedForSide = (
  chart: AnnualCombinedCurrencyChart | undefined,
  side: AnnualCombinedSide
): AnnualCombinedSideRow => {
  const row = chart?.sideRows.find((r) => r.side === side);
  return {
    side,
    pl: Number.isFinite(Number(row?.pl)) ? Number(row?.pl) : 0,
    cash: Number.isFinite(Number(row?.cash)) ? Number(row?.cash) : 0,
  };
};

const pairToCombined = (
  currency: string,
  pair: { pl?: AnnualAbsCurrencyChart; cash?: AnnualAbsCurrencyChart }
): AnnualCombinedCurrencyChart => {
  const row = (
    side: AnnualCombinedSide,
    pl: number,
    cash: number
  ): AnnualCombinedSideRow => ({
    side,
    pl: roundMoney(pl),
    cash: roundMoney(cash),
  });
  return {
    currency,
    sideRows: [
      row('In', grossForSide(pair.pl, 'Income'), grossForSide(pair.cash, 'Cash in')),
      row('Out', grossForSide(pair.pl, 'Expenses'), grossForSide(pair.cash, 'Cash out')),
      row('Net', grossForSide(pair.pl, 'Profit/Loss'), grossForSide(pair.cash, 'Net cash')),
    ],
  };
};

/** Fold every FX series into one target-currency chart (UK → GBP, IE → EUR). */
export const foldChartsIntoCurrency = (
  charts: AnnualCombinedCurrencyChart[],
  targetCurrency: string,
  gbpToEurRate: number = DEFAULT_GBP_TO_EUR_RATE
): AnnualCombinedCurrencyChart[] => {
  if (charts.length === 0) return charts;
  const target = String(targetCurrency || 'EUR').trim().toUpperCase() || 'EUR';
  const hasForeign = charts.some(
    (chart) => String(chart.currency || '').trim().toUpperCase() !== target
  );
  if (!hasForeign && charts.length === 1) return charts;

  const safeRate = eurScaleForCurrency('GBP', gbpToEurRate);
  const mergeSide = (side: AnnualCombinedSide): AnnualCombinedSideRow => {
    let pl = 0;
    let cash = 0;
    charts.forEach((chart) => {
      const scale = scaleToCurrency(chart.currency, target, safeRate);
      const row = combinedForSide(chart, side);
      pl += row.pl * scale;
      cash += row.cash * scale;
    });
    return {
      side,
      pl: roundMoney(pl),
      cash: roundMoney(cash),
    };
  };

  return [
    {
      currency: target,
      gbpConverted: hasForeign,
      gbpToEurRate: hasForeign ? safeRate : undefined,
      sideRows: [mergeSide('In'), mergeSide('Out'), mergeSide('Net')],
    },
  ];
};

/** Fold every FX series into one EUR chart. Cash (and P&L) GBP × rate; other non-EUR at 1:1. */
export const foldGbpIntoEurChart = (
  charts: AnnualCombinedCurrencyChart[],
  gbpToEurRate: number = DEFAULT_GBP_TO_EUR_RATE
): AnnualCombinedCurrencyChart[] => foldChartsIntoCurrency(charts, 'EUR', gbpToEurRate);

export const combineAnnualPlAndCashCharts = (
  plCharts: AnnualAbsCurrencyChart[],
  cashCharts: AnnualAbsCurrencyChart[],
  options?: { gbpToEurRate?: number; foldGbpIntoEur?: boolean; foldIntoCurrency?: string }
): AnnualCombinedCurrencyChart[] => {
  const byCurrency = new Map<string, { pl?: AnnualAbsCurrencyChart; cash?: AnnualAbsCurrencyChart }>();
  plCharts.forEach((chart) => {
    const ccy = String(chart.currency || '').trim().toUpperCase() || 'EUR';
    const acc = byCurrency.get(ccy) || {};
    acc.pl = chart;
    byCurrency.set(ccy, acc);
  });
  cashCharts.forEach((chart) => {
    const ccy = String(chart.currency || '').trim().toUpperCase() || 'EUR';
    const acc = byCurrency.get(ccy) || {};
    acc.cash = chart;
    byCurrency.set(ccy, acc);
  });

  const combined = Array.from(byCurrency.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, pair]) => pairToCombined(currency, pair));

  if (options?.foldGbpIntoEur === false) return combined;
  const target = String(options?.foldIntoCurrency || 'EUR').trim().toUpperCase() || 'EUR';
  return foldChartsIntoCurrency(combined, target, options?.gbpToEurRate ?? DEFAULT_GBP_TO_EUR_RATE);
};

/** Summary P&L vs Cash chart: plot Out below zero (stored totals stay positive). */
export const annualCombinedChartDisplayRows = (
  sideRows: AnnualCombinedSideRow[]
): Array<{ side: AnnualCombinedSide; pl: number | null; cash: number | null }> =>
  sideRows.map((row) => {
    const plotted =
      row.side === 'Out'
        ? { ...row, pl: -Math.abs(row.pl), cash: -Math.abs(row.cash) }
        : row;
    return {
      side: plotted.side,
      pl: plotted.pl === 0 ? null : plotted.pl,
      cash: plotted.cash === 0 ? null : plotted.cash,
    };
  });

/** Section category/supplier charts: omit zero amounts so Recharts does not draw minPointSize stubs. */
export const signedInOutChartDisplayRows = (
  rows: Array<{
    label: string;
    In: number;
    Out: number;
    inCount: number;
    outCount: number;
  }>
): Array<{
  label: string;
  In: number | null;
  Out: number | null;
  inCount: number;
  outCount: number;
}> =>
  rows
    .filter((row) => row.In !== 0 || row.Out !== 0)
    .map((row) => ({
      ...row,
      In: row.In === 0 ? null : row.In,
      Out: row.Out === 0 ? null : row.Out,
    }));

/** Annual Summary: merge cash-in / cash-out category sections onto one diverging chart. */
export const ANNUAL_IN_OUT_CHART_PAIRS = [
  { inKey: 'internal_in', outKey: 'internal_out', key: 'internal', title: 'Internal transfers' },
  { inKey: 'financing_in', outKey: 'financing_out', key: 'financing', title: 'Financing' },
  { inKey: 'capital_in', outKey: 'capital_out', key: 'capital', title: 'Property purchases' },
  { inKey: 'tax_in', outKey: 'tax_out', key: 'tax', title: 'Tax' },
  { inKey: 'reversal_in', outKey: 'reversal_out', key: 'reversal', title: 'Reversals' },
] as const;

export type AnnualCategoryGrossLine = {
  category: string;
  count: number;
  gross: number;
};

export type AnnualCategoryCurrencySummary = {
  currency: string;
  lines: AnnualCategoryGrossLine[];
  count: number;
  gross: number;
};

/** Summary category columns: VRBO guest income is short-term rental, not its own P&L category. */
export const ANNUAL_SUMMARY_SHORT_TERM_RENTAL_CATEGORY = 'Rental Income (Short-term)';

export const annualSummaryChartCategory = (category: string | null | undefined): string => {
  const raw = String(category || '').trim() || '—';
  if (raw.toLowerCase() === 'vrbo') return ANNUAL_SUMMARY_SHORT_TERM_RENTAL_CATEGORY;
  return raw;
};

export const applyAnnualSummaryCategoryAliases = (
  summaries: AnnualCategoryCurrencySummary[]
): AnnualCategoryCurrencySummary[] => {
  return summaries.map((summary) => {
    const byCat = new Map<string, AnnualCategoryGrossLine>();
    summary.lines.forEach((line) => {
      const category = annualSummaryChartCategory(line.category);
      const existing = byCat.get(category);
      const count = Number(line.count) || 0;
      const gross = Number(line.gross) || 0;
      if (!existing) {
        byCat.set(category, { category, count, gross: roundMoney(gross) });
        return;
      }
      existing.count += count;
      existing.gross = roundMoney(existing.gross + gross);
    });
    const lines = Array.from(byCat.values()).sort(
      (a, b) => b.gross - a.gross || a.category.localeCompare(b.category)
    );
    return {
      ...summary,
      lines,
      count: lines.reduce((n, line) => n + line.count, 0),
      gross: roundMoney(lines.reduce((n, line) => n + line.gross, 0)),
    };
  });
};

export type AnnualSignedInOutCategoryRow = {
  category: string;
  In: number;
  Out: number;
  inCount: number;
  outCount: number;
};

export type AnnualSummarySectionLike<TSummary, TRow = unknown> = {
  key: string;
  title: string;
  rows: TRow[];
  summaries: TSummary[];
};

export type GroupedAnnualSummarySection<TSummary, TRow = unknown> =
  | {
      paired: false;
      key: string;
      title: string;
      rows: TRow[];
      summaries: TSummary[];
    }
  | {
      paired: true;
      key: string;
      title: string;
      rows: TRow[];
      inSummaries: TSummary[];
      outSummaries: TSummary[];
    };

/** In stays positive; Out is stored negative so bars sit below zero. */
export const buildSignedInOutCategoryRows = (
  inLines: AnnualCategoryGrossLine[],
  outLines: AnnualCategoryGrossLine[]
): AnnualSignedInOutCategoryRow[] => {
  const byCategory = new Map<
    string,
    { In: number; Out: number; inCount: number; outCount: number }
  >();
  inLines.forEach((line) => {
    const category = annualSummaryChartCategory(line.category);
    const acc = byCategory.get(category) || { In: 0, Out: 0, inCount: 0, outCount: 0 };
    acc.In += Number(line.gross) || 0;
    acc.inCount += Number(line.count) || 0;
    byCategory.set(category, acc);
  });
  outLines.forEach((line) => {
    const category = annualSummaryChartCategory(line.category);
    const acc = byCategory.get(category) || { In: 0, Out: 0, inCount: 0, outCount: 0 };
    acc.Out += Number(line.gross) || 0;
    acc.outCount += Number(line.count) || 0;
    byCategory.set(category, acc);
  });
  return Array.from(byCategory.entries())
    .map(([category, acc]) => ({
      category,
      In: roundMoney(acc.In),
      Out: roundMoney(-acc.Out),
      inCount: acc.inCount,
      outCount: acc.outCount,
    }))
    .sort(
      (a, b) =>
        Math.abs(b.In) + Math.abs(b.Out) - (Math.abs(a.In) + Math.abs(a.Out)) ||
        a.category.localeCompare(b.category)
    );
};

export const groupAnnualSummaryInOutSections = <TSummary, TRow>(
  sections: AnnualSummarySectionLike<TSummary, TRow>[]
): GroupedAnnualSummarySection<TSummary, TRow>[] => {
  const pairBySectionKey = new Map<string, (typeof ANNUAL_IN_OUT_CHART_PAIRS)[number]>();
  ANNUAL_IN_OUT_CHART_PAIRS.forEach((pair) => {
    pairBySectionKey.set(pair.inKey, pair);
    pairBySectionKey.set(pair.outKey, pair);
  });
  const consumed = new Set<string>();
  const grouped: GroupedAnnualSummarySection<TSummary, TRow>[] = [];
  sections.forEach((section) => {
    if (consumed.has(section.key)) return;
    const pair = pairBySectionKey.get(section.key);
    if (!pair) {
      grouped.push({
        paired: false,
        key: section.key,
        title: section.title,
        rows: section.rows,
        summaries: section.summaries,
      });
      return;
    }
    consumed.add(pair.inKey);
    consumed.add(pair.outKey);
    const inSection = sections.find((s) => s.key === pair.inKey);
    const outSection = sections.find((s) => s.key === pair.outKey);
    grouped.push({
      paired: true,
      key: pair.key,
      title: pair.title,
      rows: [...(inSection?.rows || []), ...(outSection?.rows || [])],
      inSummaries: inSection?.summaries || [],
      outSummaries: outSection?.summaries || [],
    });
  });
  return grouped;
};

export type AnnualSignedInOutSupplierRow = {
  supplier: string;
  In: number;
  Out: number;
  inCount: number;
  outCount: number;
};

export const buildSignedInOutSupplierRows = (
  inLines: Array<{ supplier: string; gross: number; count: number }>,
  outLines: Array<{ supplier: string; gross: number; count: number }>
): AnnualSignedInOutSupplierRow[] => {
  const bySupplier = new Map<
    string,
    { In: number; Out: number; inCount: number; outCount: number }
  >();
  inLines.forEach((line) => {
    const supplier = String(line.supplier || '').trim() || '—';
    const acc = bySupplier.get(supplier) || { In: 0, Out: 0, inCount: 0, outCount: 0 };
    acc.In += Number(line.gross) || 0;
    acc.inCount += Number(line.count) || 0;
    bySupplier.set(supplier, acc);
  });
  outLines.forEach((line) => {
    const supplier = String(line.supplier || '').trim() || '—';
    const acc = bySupplier.get(supplier) || { In: 0, Out: 0, inCount: 0, outCount: 0 };
    acc.Out += Number(line.gross) || 0;
    acc.outCount += Number(line.count) || 0;
    bySupplier.set(supplier, acc);
  });
  return Array.from(bySupplier.entries())
    .map(([supplier, acc]) => ({
      supplier,
      In: roundMoney(acc.In),
      Out: roundMoney(-acc.Out),
      inCount: acc.inCount,
      outCount: acc.outCount,
    }))
    .sort(
      (a, b) =>
        Math.abs(b.In) + Math.abs(b.Out) - (Math.abs(a.In) + Math.abs(a.Out)) ||
        a.supplier.localeCompare(b.supplier)
    );
};

export type SupplierColumnChartRow = {
  supplier: string;
  Gross: number;
  Net: number;
  VAT: number;
  labelTotal: number;
  count: number;
};

export type SupplierColumnCurrencyChart = {
  currency: string;
  suppliers: SupplierColumnChartRow[];
  gbpConverted?: boolean;
  gbpToEurRate?: number;
};

/** Same GBP→EUR fold as Summary so section supplier charts are one EUR series. */
export const foldGbpIntoEurSupplierCharts = (
  charts: SupplierColumnCurrencyChart[],
  gbpToEurRate: number = DEFAULT_GBP_TO_EUR_RATE
): SupplierColumnCurrencyChart[] => {
  if (charts.length === 0) return charts;
  const hasForeign = charts.some(
    (chart) => String(chart.currency || '').trim().toUpperCase() !== 'EUR'
  );
  if (!hasForeign && charts.length === 1) return charts;

  const safeRate = eurScaleForCurrency('GBP', gbpToEurRate);
  const byName = new Map<string, SupplierColumnChartRow>();
  const add = (row: SupplierColumnChartRow, scale: number) => {
    const name = String(row.supplier || '').trim() || '—';
    const existing = byName.get(name);
    const gross = (Number(row.Gross) || 0) * scale;
    const net = (Number(row.Net) || 0) * scale;
    const vat = (Number(row.VAT) || 0) * scale;
    if (!existing) {
      byName.set(name, {
        supplier: name,
        Gross: roundMoney(gross),
        Net: roundMoney(net),
        VAT: roundMoney(vat),
        labelTotal: roundMoney(gross),
        count: Number(row.count) || 0,
      });
      return;
    }
    existing.Gross = roundMoney(existing.Gross + gross);
    existing.Net = roundMoney(existing.Net + net);
    existing.VAT = roundMoney(existing.VAT + vat);
    existing.labelTotal = existing.Gross;
    existing.count += Number(row.count) || 0;
  };
  charts.forEach((chart) => {
    const scale = eurScaleForCurrency(chart.currency, safeRate);
    chart.suppliers.forEach((row) => add(row, scale));
  });

  return [
    {
      currency: 'EUR',
      gbpConverted: hasForeign,
      gbpToEurRate: hasForeign ? safeRate : undefined,
      suppliers: Array.from(byName.values()).sort(
        (a, b) => b.Gross - a.Gross || a.supplier.localeCompare(b.supplier)
      ),
    },
  ];
};

/** One chart in `targetCurrency`: convert via EUR (GBP × 1.16). */
export const foldCategorySummariesIntoCurrency = (
  summaries: AnnualCategoryCurrencySummary[],
  targetCurrency: string,
  gbpToEurRate: number = DEFAULT_GBP_TO_EUR_RATE
): AnnualCategoryCurrencySummary[] => {
  if (summaries.length === 0) return summaries;
  const target = String(targetCurrency || 'EUR').trim().toUpperCase() || 'EUR';
  const hasForeign = summaries.some(
    (summary) => String(summary.currency || '').trim().toUpperCase() !== target
  );
  if (!hasForeign && summaries.length === 1) {
    return applyAnnualSummaryCategoryAliases(summaries);
  }

  const safeRate = eurScaleForCurrency('GBP', gbpToEurRate);
  const byCat = new Map<string, AnnualCategoryGrossLine>();
  let count = 0;
  summaries.forEach((summary) => {
    const scale = scaleToCurrency(summary.currency, target, safeRate);
    count += Number(summary.count) || 0;
    summary.lines.forEach((line) => {
      const category = annualSummaryChartCategory(line.category);
      const gross = (Number(line.gross) || 0) * scale;
      const existing = byCat.get(category);
      if (!existing) {
        byCat.set(category, {
          category,
          count: Number(line.count) || 0,
          gross: roundMoney(gross),
        });
        return;
      }
      existing.count += Number(line.count) || 0;
      existing.gross = roundMoney(existing.gross + gross);
    });
  });
  const lines = Array.from(byCat.values()).sort(
    (a, b) => b.gross - a.gross || a.category.localeCompare(b.category)
  );
  return [
    {
      currency: target,
      lines,
      count,
      gross: roundMoney(lines.reduce((n, line) => n + line.gross, 0)),
    },
  ];
};

export const foldCategorySummariesIntoEur = (
  summaries: AnnualCategoryCurrencySummary[],
  gbpToEurRate: number = DEFAULT_GBP_TO_EUR_RATE
): AnnualCategoryCurrencySummary[] =>
  foldCategorySummariesIntoCurrency(summaries, 'EUR', gbpToEurRate);

